import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog, DialogTitle } from '@mui/material';

import { loadClaimQueueActionStatusDetails } from '../../../store/directusService';
import { updateClaimQueueActionStatus } from '../../../store/participantService';
import {
    ADVISER,
    hasRole,
    MANAGER,
    PRAP,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import SingleSelect from '../../formElements/SingleSelect';
import TextAreaField from '../../formElements/TextAreaField';

import { NOTE_MAX_CHAR, validate } from './validateActionEditor';

import form from '../../../commonStyles/formStyles.module.css';
import modal from '../../../commonStyles/modal.module.css';

const UPDATE_ACTION_STATUS_MESSAGE = 'Claim queue action status has been updated';

const catCarEscalatedRoles = [MANAGER];
const catOnlyRoles = [ADVISER, RECRUITMENT_MANAGER];
const acceptedRoles = [PRAP, QUALITY, SUPERUSER];
const allRoles = [...catOnlyRoles, ...catCarEscalatedRoles, ...acceptedRoles];

const ActionEditor = ({ roles, isOpen, onCancel, formType, onUpdateAction, selectedRow }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = { id: '', actionStatusId: '', actionNotes: '' };
    const [actionStatuses, setActionStatuses] = useState([]);
    const [newEntry, setNewEntry] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const claimQueueActionStatusDetails = useSelector(
        (state) => state.entities.directusService.claimQueueActionStatusDetails
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        claimQueueActionStatusDetails?.length < 1 && dispatch(loadClaimQueueActionStatusDetails());
    }, []);

    useEffect(() => {
        if (roles.length < 1 || claimQueueActionStatusDetails?.length < 1) return;

        const catStatus = claimQueueActionStatusDetails.find(
            (el) => el.name === 'Corrective Action Taken'
        );
        const carStatus = claimQueueActionStatusDetails.find(
            (el) => el.name === 'Corrective Action Required'
        );
        const carEscalatedStatus = claimQueueActionStatusDetails.find(
            (el) => el.name === 'Escalate Corrective Action Required'
        );

        setNewEntry((prev) => ({
            ...prev,
            id: selectedRow.id,
            actionStatusId: formType === 'create' ? carStatus.id : selectedRow.actionStatusId || ''
        }));

        if (formType === 'edit') {
            if (roles.some((el) => acceptedRoles.includes(el)))
                setActionStatuses(claimQueueActionStatusDetails);
            if (roles.some((el) => catOnlyRoles.includes(el))) setActionStatuses([catStatus]);
            if (roles.some((el) => catCarEscalatedRoles.includes(el)))
                setActionStatuses([carEscalatedStatus, catStatus]);
        }
    }, [roles, claimQueueActionStatusDetails]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === UPDATE_ACTION_STATUS_MESSAGE) {
            onUpdateAction();
        }
    }, [successMessage]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({
            ...prev,
            [key]: value
        }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(newEntry);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        dispatch(updateClaimQueueActionStatus(newEntry, UPDATE_ACTION_STATUS_MESSAGE));
    };

    // RENDER
    return (
        <div>
            <Dialog onClose={onCancel} open={isOpen} fullWidth={true}>
                <div className={modal.modalHeader}>
                    <DialogTitle>
                        {formType === 'edit' ? 'Update Action' : 'Create Action'}
                    </DialogTitle>
                </div>
                <div className={modal.modalContent}>
                    <form
                        onSubmit={onSubmit}
                        className={form.formWrapper}
                        data-testid="update_action_form_start">
                        {formType === 'create' ? (
                            <div className={form.staticField}>
                                <h3>Corrective Activity Status</h3>
                                Corrective Activity Required
                            </div>
                        ) : (
                            <SingleSelect
                                id={'actionStatusId'}
                                label={'Corrective Activity Status'}
                                placeholder={'Select an Action Status'}
                                disabled={!hasRole(allRoles, roles)}
                                mandatory={true}
                                menuItems={actionStatuses || []}
                                selectedId={
                                    newEntry.actionStatusId || selectedRow?.actionStatusId || ''
                                }
                                selected={actionStatuses.find(
                                    (el) => el.id === newEntry.actionStatusId
                                )}
                                error={errors.actionStatusId}
                                onChange={(chosenId) => onUpdate('actionStatusId', chosenId)}
                            />
                        )}

                        <TextAreaField
                            id="actionNotes"
                            label="Notes"
                            placeholder={'Notes'}
                            disabled={!hasRole(allRoles, roles)}
                            mandatory={true}
                            maxLength={NOTE_MAX_CHAR}
                            rows={8}
                            value={newEntry.actionNotes || ''}
                            count={`${newEntry.actionNotes.length}/${NOTE_MAX_CHAR}`}
                            error={errors.actionNotes}
                            onChange={(e) => onUpdate('actionNotes', e.target.value)}
                        />

                        <div className={modal.actionButtons}>
                            <Button
                                id="actionEditorSubmitButton"
                                disabled={!hasRole(allRoles, roles) || isButtonDisabled}
                                content={formType === 'create' ? 'Create' : 'Update'}
                            />

                            <Button
                                id="actionEditorCancelButton"
                                type="button"
                                disabled={!hasRole(allRoles, roles)}
                                content="Cancel"
                                onClick={onCancel}
                            />
                        </div>
                    </form>
                </div>
            </Dialog>
        </div>
    );
};

ActionEditor.propTypes = {
    roles: PropTypes.arrayOf(PropTypes.string),
    onCancel: PropTypes.func,
    isOpen: PropTypes.bool,
    formType: PropTypes.string,
    onUpdateAction: PropTypes.func,
    selectedRow: PropTypes.object
};

export default ActionEditor;
